import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

const NAV = [
    { label: "Services", to: "/services" },
    { label: "Industries", to: "/industries" },
    { label: "Pricing", to: "/pricing" },
    { label: "Track", to: "/track" },
    { label: "Resources", to: "/resources" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled ? "glass" : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16 sm:h-20">
                    <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo-link">
                        <LogoMark />
                        <span className="font-display text-white text-lg sm:text-xl tracking-tight">
                            WE&nbsp;DELVR LOGISTICS
                        </span>
                    </Link>

                    <nav className="hidden lg:flex items-center gap-1">
                        {NAV.map((item) => (
                            <NavLink key={item.to} {...item} active={location.pathname === item.to} />
                        ))}
                        <a
                            href={location.pathname === "/" ? "#contact" : "/#contact"}
                            data-testid="nav-link-contact"
                            className="px-3 py-2 rounded-md text-sm text-zinc-400 hover:text-white transition-colors"
                        >
                            Contact
                        </a>
                    </nav>

                    <div className="hidden lg:flex items-center gap-3">
                        <Link to="/demo" className="text-sm text-zinc-300 hover:text-cyan-300 transition-colors" data-testid="nav-demo-link">
                            Book a demo
                        </Link>
                        <Link to="/#early-access" className="btn-primary text-sm py-2.5 px-5" data-testid="nav-cta-register">
                            Join Early Access
                            <span className="pulse-dot" />
                        </Link>
                    </div>

                    <button
                        onClick={() => setOpen((v) => !v)}
                        className="lg:hidden text-white p-2"
                        aria-label="Toggle menu"
                        data-testid="nav-mobile-toggle"
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden glass border-t border-white/5" data-testid="nav-mobile-menu">
                    <div className="px-6 py-6 flex flex-col gap-4">
                        {NAV.map((item) => (
                            <Link
                                key={item.to}
                                to={item.to}
                                className="text-zinc-300 text-base"
                                data-testid={`nav-mobile-${item.label.toLowerCase()}`}
                            >
                                {item.label}
                            </Link>
                        ))}
                        <a
                            href={location.pathname === "/" ? "#contact" : "/#contact"}
                            className="text-zinc-300 text-base"
                            data-testid="nav-mobile-contact"
                        >
                            Contact
                        </a>
                        <Link to="/demo" className="btn-secondary justify-center mt-2">
                            Book a demo
                        </Link>
                        <Link to="/#early-access" className="btn-primary justify-center">
                            Join Early Access
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}

function NavLink({ label, to, active }) {
    return (
        <Link
            to={to}
            data-testid={`nav-link-${label.toLowerCase()}`}
            className={`px-3 py-2 rounded-md text-sm transition-colors ${
                active ? "text-cyan-300" : "text-zinc-400 hover:text-white"
            }`}
        >
            {label}
        </Link>
    );
}

function LogoMark() {
    return (
        <span
            aria-hidden
            className="relative inline-flex items-center justify-center w-8 h-8 rounded-md"
            style={{
                background: "linear-gradient(135deg, rgba(0,240,255,0.25), rgba(0,240,255,0.05))",
                border: "1px solid rgba(0,240,255,0.45)",
                boxShadow: "inset 0 0 12px rgba(0,240,255,0.35)",
            }}
        >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                    d="M2 4l6 4 6-4M2 8l6 4 6-4"
                    stroke="#00F0FF"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </span>
    );
}

// Keep ChevronDown import alive if needed for future nested menus
void ChevronDown;
